import { useIMask } from 'react-imask';
import { useEffect, useMemo, useState } from 'react';

import { Input, IInputProps } from '../input';
import { useIsFirstRender } from '../../hooks/use-is-first-render';

interface INumberInputProps extends Omit<IInputProps, 'onChange' | 'value'> {
  value: string;
  onChange?: (value: string) => void;
  mode?: 'apartment';
  min?: number;
  max?: number;
  sign?: string;
  decimal?: boolean;
  withSeparator?: boolean;
  format?: (value: string) => string;
}

const NumberInput: React.FC<INumberInputProps> = (props) => {
  const {
    min,
    max,
    sign = '',
    mode,
    value,
    format,
    decimal,
    onChange,
    placeholder,
    withSeparator = true,
    ...inputProps
  } = props;

  const opts = useMemo(
    () =>
      ({
        mask: [
          { mask: '' },
          {
            mask: sign ? `num ${sign}` : 'num',
            lazy: false,
            expose: true,
            blocks: {
              num: {
                min,
                max,
                mask: Number,
                expose: true,
                thousandsSeparator: withSeparator ? ' ' : '',
                overwrite: true,
                ...(decimal
                  ? {
                      scale: 2,
                      radix: '.',
                      mapToRadix: [','],
                      padFractionalZeros: true,
                      normalizeZeros: true,
                    }
                  : { scale: 0 }),
              },
            },
          },
        ],
      } as Parameters<typeof useIMask>[0]),
    [sign, min, max, decimal],
  );

  const defaultValue = useState(() => (value ? format?.(value) : ''))[0];

  const isFirstRender = useIsFirstRender();

  const { ref, maskRef, unmaskedValue, setUnmaskedValue } = useIMask(opts, {
    onComplete(_maskedValue, maskRef) {
      if (onChange && !isFirstRender) {
        onChange(maskRef.unmaskedValue);
      }
    },
  });

  useEffect(() => {
    if (value !== unmaskedValue) {
      setUnmaskedValue(value);
    }
  }, [value]);

  const handleChangeApartmentNumber = (value: string) => {
    if (onChange) {
      if (value === '' || value === '0') {
        return onChange('');
      }

      const isNumber = /^(?:[1-9]\d*|0)$/;

      if (isNumber.test(value)) {
        if (length) {
          if (value.length <= length) {
            return onChange(value);
          }
          return;
        }

        return onChange(value);
      }
    }
  };

  return (
    <Input
      inputRef={ref}
      placeholder={placeholder || `0 ${sign}`}
      defaultValue={defaultValue}
      showClearButton={!!value}
      onChange={(val) => {
        if (mode === 'apartment') {
          return handleChangeApartmentNumber(val);
        }
        onChange && onChange(val);
      }}
      {...inputProps}
      value={value}
    />
  );
};

export default NumberInput;
